import { BUILTIN_TOOL_DEFINITIONS } from "@/data/toolDefinitions";
import type { ToolDefinition, ToolExecutor, ToolInvokeContext, ToolInvokeResult } from "@/types/toolRegistry";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { chatThreadsService } from "@/services/chatThreadsService";
import { ragService } from "@/services/ragService";
import { integrationRagService } from "@/services/integrationRagService";
import { clickupService } from "@/services/clickupService";
import type { CreateClickUpTaskRequest } from "@/types/clickup";
import { gmailService } from "@/services/gmailService";
import { telegramService } from "@/services/telegramService";
import { hasGmailModifyScope } from "@/lib/googleAuth";
import type { IntegrationSource } from "@/lib/integrationRag/types";

const executors = new Map<string, ToolExecutor>();

function registerBuiltinExecutors(): void {
  executors.set("thread", async (operation, input, ctx) => {
    if (operation !== "create") return { success: false, error: "Unknown operation" };
    try {
      const thread = await chatThreadsService.createChatThread({
        title: (input.title as string) || "Workflow Thread",
        messages: input.content
          ? [
              { role: "system", content: "Context from workflow run." },
              { role: "user", content: String(input.content) },
            ]
          : [],
        linkedSpaceIds: [],
        contextMode: "MAX",
        chatMode: "normal",
        agentId: (input.agentId as string) || null,
      } as Parameters<typeof chatThreadsService.createChatThread>[0]);
      return {
        success: true,
        output: { threadId: thread.id },
        artifact: {
          artifactType: "thread",
          threadId: thread.id,
          title: thread.title ?? "Workflow Thread",
          url: `/chat?thread=${thread.id}`,
        },
      };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : "Thread create failed" };
    }
  });

  executors.set("kb", async (operation, input) => {
    if (operation !== "create_document") return { success: false, error: "Unknown operation" };
    try {
      const doc = await knowledgeBaseService.createKnowledgeBaseItem({
        title: (input.title as string) || "Workflow Document",
        content: (input.content as string) || "",
        type: "note",
        spaceId: (input.spaceId as string) || null,
        topicId: (input.topicId as string) || null,
        includeInRag: input.includeInRag !== false,
      });
      return {
        success: true,
        output: { documentId: doc.id },
        artifact: {
          artifactType: "kb_document",
          kbDocumentId: doc.id,
          title: doc.title,
          url: `/library/${doc.id}`,
        },
      };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : "KB create failed" };
    }
  });

  executors.set("rag", async (operation, input) => {
    try {
      if (operation === "index_kb" && input.documentId) {
        await ragService.embedDocument(String(input.documentId));
        return {
          success: true,
          artifact: { artifactType: "rag_index", metadata: { target: "kb", documentId: input.documentId } },
        };
      }
      if (operation === "index_thread" && input.threadId) {
        await ragService.embedThread(String(input.threadId));
        return {
          success: true,
          artifact: { artifactType: "rag_index", metadata: { target: "thread", threadId: input.threadId } },
        };
      }
      if (operation === "index_integration" && input.source && input.externalId && input.content) {
        await integrationRagService.indexItems([
          {
            source: input.source as IntegrationSource,
            externalId: String(input.externalId),
            title: String(input.title ?? input.externalId),
            content: String(input.content),
          },
        ]);
        return {
          success: true,
          artifact: {
            artifactType: "rag_index",
            externalId: String(input.externalId),
            metadata: { target: "integration", source: input.source },
          },
        };
      }
      return { success: false, error: "Invalid RAG index input" };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : "RAG index failed" };
    }
  });

  executors.set("clickup", async (operation, input) => {
    if (operation !== "create_task") return { success: false, error: "Unknown operation" };
    try {
      const listId = input.listId as string;
      const tasks = input.tasks as unknown[];
      if (!listId || !Array.isArray(tasks)) return { success: false, error: "listId and tasks required" };
      const requests = tasks.map((task) => ({
        listId,
        ...(task as Omit<CreateClickUpTaskRequest, "listId">),
      })) as CreateClickUpTaskRequest[];

      const result = await clickupService.createTasks(requests);
      return {
        success: true,
        output: result,
        artifact: {
          artifactType: "clickup_task",
          title: `ClickUp tasks (${tasks.length})`,
          metadata: { listId, result },
        },
      };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : "ClickUp failed" };
    }
  });

  executors.set("gmail", async (operation, input) => {
    const writeOps = ["trash", "archive", "create_draft", "apply_label"];
    if (writeOps.includes(operation)) {
      const hasWrite = await hasGmailModifyScope();
      if (!hasWrite) {
        return {
          success: false,
          error: "Gmail write not permitted",
          needsPermission: true,
        };
      }
    }

    const messageId = String(input.messageId ?? "");

    try {
      if (operation === "trash") {
        if (!messageId) return { success: false, error: "messageId required" };
        await gmailService.trashMessage(messageId);
        return {
          success: true,
          output: { messageId, trashed: true },
          artifact: {
            artifactType: "email_label",
            gmailId: messageId,
            metadata: { operation: "trash" },
          },
        };
      }
      if (operation === "archive") {
        if (!messageId) return { success: false, error: "messageId required" };
        await gmailService.archiveMessage(messageId);
        return {
          success: true,
          output: { messageId, archived: true },
          artifact: {
            artifactType: "email_label",
            gmailId: messageId,
            metadata: { operation: "archive" },
          },
        };
      }
      if (operation === "create_draft") {
        const draft = await gmailService.createDraft({
          to: String(input.to ?? ""),
          subject: String(input.subject ?? ""),
          body: String(input.body ?? ""),
          threadId: input.threadId ? String(input.threadId) : undefined,
        });
        return {
          success: true,
          output: draft,
          artifact: {
            artifactType: "gmail_draft",
            gmailId: draft.draftId,
            metadata: { operation: "create_draft" },
          },
        };
      }
      if (operation === "apply_label") {
        return { success: false, error: "apply_label not implemented" };
      }
      return { success: false, error: `Unknown Gmail operation: ${operation}` };
    } catch (e) {
      const message = e instanceof Error ? e.message : "Gmail operation failed";
      const needsPermission = /insufficient|permission|scope|403/i.test(message);
      return { success: false, error: message, needsPermission };
    }
  });

  executors.set("telegram", async (operation, input) => {
    if (operation !== "send_message") return { success: false, error: "Unknown operation" };
    try {
      const chatId = String(input.chatId ?? "");
      const text = String(input.text ?? "");
      if (!chatId || !text) return { success: false, error: "chatId and text required" };
      const result = await telegramService.sendMessage(chatId, text);
      return {
        success: true,
        output: result,
        artifact: {
          artifactType: "telegram_message",
          metadata: { chatId, messageId: result.messageId },
        },
      };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : "Telegram send failed" };
    }
  });

  executors.set("http", async (operation, input) => {
    if (operation !== "request") return { success: false, error: "Unknown operation" };
    try {
      const res = await fetch(String(input.url), {
        method: (input.method as string) || "GET",
        headers: { "Content-Type": "application/json" },
        body: input.body ? String(input.body) : undefined,
      });
      const text = await res.text();
      return { success: res.ok, output: { status: res.status, body: text } };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : "HTTP failed" };
    }
  });
}

registerBuiltinExecutors();

class ToolRegistryService {
  private definitions = new Map<string, ToolDefinition>();

  constructor() {
    for (const def of BUILTIN_TOOL_DEFINITIONS) {
      this.definitions.set(def.id, def);
    }
  }

  register(definition: ToolDefinition, executor?: ToolExecutor): void {
    this.definitions.set(definition.id, definition);
    if (executor) executors.set(definition.executorKey, executor);
  }

  unregister(toolId: string): void {
    const def = this.definitions.get(toolId);
    if (!def || def.isBuiltin) return;
    this.definitions.delete(toolId);
    executors.delete(def.executorKey);
  }

  getDefinition(toolId: string): ToolDefinition | undefined {
    return this.definitions.get(toolId);
  }

  listDefinitions(): ToolDefinition[] {
    return Array.from(this.definitions.values());
  }

  async invoke(
    toolId: string,
    operation: string,
    input: Record<string, unknown>,
    context: ToolInvokeContext
  ): Promise<ToolInvokeResult> {
    const def = this.definitions.get(toolId);
    if (!def) return { success: false, error: `Unknown tool: ${toolId}` };
    const op = def.operations.find((o) => o.name === operation);
    if (!op) return { success: false, error: `Unknown operation: ${operation}` };
    const executor = executors.get(def.executorKey);
    if (!executor) return { success: false, error: `No executor for ${def.executorKey}` };
    return executor(operation, input, context);
  }
}

export const toolRegistry = new ToolRegistryService();
